import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, IHostWebsite, ITournament, ITournamentEdition, OmneHostService, SanitizedResponse, TournamentStatus } from "../../../../services";
import { Classname, DateHelper, Paginator, Scheme, StringHelper, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";
import { i18nManager } from "../../../../i18n";


interface TournamentEdition {
    edition: number;
    year: number;
    winner: string;
    prizePool: string;
}
interface Tournament {
    id: number;
    name: string;
    logo: string;
    banner: string;
    status: TournamentStatus;
    prizePool: string;
    playersPerTeam: number;
    game: string;
    date: string;
    registeredTeams: number;
    maxTeams: number;
    registerLink: string;
    editions: TournamentEdition[];
    description: string;
    location: string;
}

interface TournamentCardProps {
    tournament: ITournament;
    onSelect: (t: ITournament) => void;
}

export function TournamentCard({ tournament, onSelect }: TournamentCardProps) {

    const tournamentEdition = tournament.tournament_edition!;
    const isUpcoming = tournamentEdition.status === TournamentStatus.UPCOMING;
    const prizePool = Object.entries(tournamentEdition.meta.price_pool_amounts ?? {});
    const pct: number = Math.round((tournamentEdition.meta.total_registered_participants / tournamentEdition.meta.total_expected_participants) * 100);

    const statusClass: string = tournamentEdition.status === TournamentStatus.ONGOING
        ? styles["t-status-ongoing"]
        : tournamentEdition.status === TournamentStatus.UPCOMING
            ? styles["t-status-upcoming"]
            : styles["t-status-past"];
    const statusLabel: React.ReactNode = tournamentEdition.status === TournamentStatus.ONGOING ? (<><i className="fa fa-circle" style={{ fontSize: "0.55em", verticalAlign: "middle" }} /> Live</>) : tournamentEdition.status === TournamentStatus.UPCOMING ? "Upcoming" : (<><i className="fa fa-check" /> Past</>);

    return (
        <div
            className={`${styles["t-card"]} ${tournamentEdition.status === TournamentStatus.ONGOING ? styles["t-ongoing"] : ""}`}
            onClick={() => onSelect(tournament)}
        >
            <img className={styles["t-banner"]} src={tournamentEdition.banner_url} alt={tournament.name} loading="lazy" />

            <div className={styles["t-body"]}>
                <div className={styles["t-head"]}>
                    <img className={styles["t-logo"]} src={tournamentEdition.logo_url} alt={tournament.name} />
                    <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.2rem" }}>
                            <span className={`${styles["t-status-badge"]} ${statusClass}`}>{statusLabel}</span>
                        </div>
                        <div className={styles["t-name"]}>{tournamentEdition.name}</div>
                        <div className={styles["t-edition"]}>Edition {tournament.extra_data?.tournament_edition_count ?? 1} · {tournamentEdition.name}</div>
                    </div>
                </div>

                <div className={styles["t-meta-row"]}>
                    {(tournamentEdition.dates.tournament_start_date && tournamentEdition.dates.tournament_end_date) && (<div className={styles["t-meta-item"]}><i className="fa fa-calendar" /> <span>{Utils.rangeDate(new Date(tournamentEdition.dates.tournament_start_date), new Date(tournamentEdition.dates.tournament_end_date))}</span></div>)}
                    {(!!tournamentEdition.location) && (<div className={styles["t-meta-item"]}><i className="fa fa-location-dot" /> <span>{tournamentEdition.location}</span></div>)}
                    <div className={styles["t-meta-item"]}>Components <span>{tournamentEdition.participant_members_count}v{tournamentEdition.participant_members_count}</span></div>
                </div>

                {(!!prizePool.length) && (<>
                    <div style={{ fontSize: "0.68rem", color: "var(--muted)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "0.1rem" }}>
                        Prize Pool
                    </div>
                    {prizePool.map(([c, v]) => (<div key={c} className={styles["t-prize"]}>{i18nManager.queryCountryCurrency(c, "currency_code", "currency_sign")} {StringHelper.toPresentableMoneyValue(v)}</div>))}
                </>)}

                <div className={styles["t-capacity"]}>
                    <div className={styles["t-capacity-bar"]}>
                        <div className={styles["t-capacity-fill"]} style={{ width: `${pct}%` }} />
                    </div>
                    <div className={styles["t-capacity-txt"]}>
                        <span>{tournamentEdition.meta.total_registered_participants ?? 0} registered</span>
                        <span>{(tournamentEdition.meta.total_expected_participants ?? 0) - (tournamentEdition.meta.total_registered_participants ?? 0)} spots left</span>
                    </div>
                </div>

                <div className={styles["t-footer"]}>
                    <span style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                        {tournament.extra_data?.tournament_edition_count ?? 1} edition{(tournament.extra_data?.tournament_edition_count ?? 1) > 1 ? "s" : ""}
                    </span>
                    <ReactRouterDom.NavLink target="_blank" to={{ pathname: `/to/${tournament.public_id}`, search: "edi=" + tournamentEdition.external_id + (isUpcoming ? "&register" : "") }}
                        className={styles["btn-register"]} style={{ textDecoration: "none" }}
                        onClick={e => { e.stopPropagation(); }}
                    >
                        {isUpcoming ? (<>Register <i className="fa fa-arrow-right" /></>) : "Visit"}
                    </ReactRouterDom.NavLink>
                </div>
            </div>
        </div>
    );
}

interface TournamentModalProps {
    hostId: string;
    onClose: () => void;
    tournament: ITournament;
}

function TournamentModal({ hostId, tournament, onClose }: TournamentModalProps) {

    const omnehostService = OmneHostService.getInstance();
    const tournamentEdition = tournament.tournament_edition!;
    const prizePool = Object.entries(tournamentEdition.meta.price_pool_amounts ?? {});
    const [tournamentEditions, setTournamentEditions] = React.useState<ITournamentEdition[]>();
    const pct: number = Math.round((tournamentEdition.meta.total_registered_participants / tournamentEdition.meta.total_expected_participants) * 100);

    React.useEffect(() => {
        omnehostService.queryHostTournamentEditions(hostId, {
            size: 20,
            sort: "updated_at,DESC",
            tournament_id: tournament.external_id,
            status__in: ["UPCOMING", "ONGOING", "ENDED"]
        }).then(({ content }: SanitizedResponse<ITournamentEdition>) => {
            setTournamentEditions(content);
        }).catch(BaseService.reportError);
    }, []);

    return (
        <div className={styles["modal-overlay"]} onClick={e => e.target === e.currentTarget && onClose()}>
            <div className={styles["modal"]}>
                <button className={styles["modal-close"]} onClick={onClose}><i className="fa fa-xmark" /></button>
                <img className={styles["modal-banner-img"]} src={tournamentEdition.banner_url} alt={tournament.name} />

                <div className={styles["modal-body"]}>
                    <div className={styles["modal-head"]}>
                        <img className={styles["modal-logo"]} src={tournamentEdition.logo_url} alt={tournament.name} />
                        <div>
                            <div
                                className={`${styles["t-status-badge"]} ${tournamentEdition.status === TournamentStatus.ONGOING
                                    ? styles["t-status-ongoing"]
                                    : tournamentEdition.status === TournamentStatus.UPCOMING
                                        ? styles["t-status-upcoming"]
                                        : styles["t-status-past"]
                                    }`}
                                style={{ marginBottom: "0.3rem" }}
                            >
                                {tournamentEdition.status === TournamentStatus.ONGOING ? (<><i className="fa fa-circle" style={{ fontSize: "0.55em", verticalAlign: "middle" }} /> Live</>) : tournamentEdition.status === TournamentStatus.UPCOMING ? "Upcoming" : (<><i className="fa fa-check" /> Completed</>)}
                            </div>
                            <div className={styles["modal-title"]}>{tournament.name}</div>
                            <div style={{ color: "var(--gold)", fontSize: "0.75rem", fontWeight: 600 }}>
                                {tournamentEdition.name} · Edition {tournament.extra_data?.tournament_edition_count ?? 1}
                            </div>
                        </div>
                    </div>

                    <p style={{ color: "var(--muted)", fontSize: "0.88rem", lineHeight: 1.7, marginBottom: "1rem" }}>
                        {tournament.description}
                    </p>

                    <div className={styles["modal-info-grid"]}>
                        {([
                            ["Team Size", `${tournamentEdition.participant_members_count}v${tournamentEdition.participant_members_count}`],
                            ["Location", tournamentEdition.location],
                            ["Date", (tournamentEdition.dates.tournament_start_date && tournamentEdition.dates.tournament_end_date ? Utils.rangeDate(new Date(tournamentEdition.dates.tournament_start_date), new Date(tournamentEdition.dates.tournament_end_date)) : null)],
                            ["Registered", `${tournamentEdition.meta.total_registered_participants}/${tournamentEdition.meta.total_expected_participants}`],
                            ["Capacity", `${pct}%`],
                            ["Program", tournamentEdition.meta.program_name]
                        ] as [string, string][]).filter(([l, v]) => !!v).map(([l, v]) => (
                            <div key={l} className={styles["modal-info-box"]}>
                                <div className={styles["modal-info-val"]} style={{ fontSize: "0.95rem", fontFamily: "'DM Sans',sans-serif", fontWeight: 700 }}>
                                    {v}
                                </div>
                                <div className={styles["modal-info-lbl"]}>{l}</div>
                            </div>
                        ))}
                    </div>

                    {(!!prizePool.length) && (<>
                        <div style={{ fontSize: "0.68rem", color: "var(--muted)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "0.1rem" }}>
                            Prize Pool
                        </div>
                        {prizePool.map(([c, v], i) => (<span className={styles["t-prize"]} key={c}>
                            {i18nManager.queryCountryCurrency(c, "currency_code", "currency_sign")} {StringHelper.toPresentableMoneyValue(v)}
                            {(i != (prizePool.length - 1)) && (" - ")}
                        </span>))}
                    </>)}

                    <div className={styles["t-capacity"]} style={{ marginTop: "1.5rem", marginBottom: "1.5rem" }}>
                        <div className={styles["t-capacity-bar"]} style={{ height: "8px" }}>
                            <div className={styles["t-capacity-fill"]} style={{ width: `${pct}%` }} />
                        </div>
                    </div>

                    {tournamentEdition.status !== TournamentStatus.ENDED && (
                        <button className={styles["btn-primary"]} style={{ marginBottom: "2rem" }}>
                            Register Your Team <i className="fa fa-arrow-right" />
                        </button>
                    )}

                    {(!!tournamentEditions?.length) && (<>
                        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.2rem", letterSpacing: "2px", color: "var(--gold)", marginTop: "2.75rem", marginBottom: "0.75rem" }}>
                            EDITION HISTORY
                        </div>
                        <table className={styles["editions-table"]}>
                            <thead>
                                <tr>
                                    <th>Edition</th>
                                    <th>Year</th>
                                    <th>Winner</th>
                                    <th>Prize</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tournamentEditions.map((ed, i) => {

                                    const winnerCurrency = ed.meta.winner_currency;
                                    const winnerPrizePoolAmount = ed.meta.price_pool_amounts?.[winnerCurrency ?? ""] ?? 0;

                                    return (<tr key={ed.external_id}>
                                        <td><ReactRouterDom.NavLink target="_blank" to={{ pathname: `/to/${tournament.public_id}`, search: "edi=" + ed.external_id }} style={{ textDecoration: "none", color: "var(--gold)" }}>#{i + 1} {ed.name}</ReactRouterDom.NavLink></td>
                                        <td>{ed.dates.tournament_start_date ? Utils.fineDate(new Date(ed.dates.tournament_start_date)) : DateHelper.now().getFullYear()}</td>
                                        <td className={styles["winner-col"]}>{ed.meta.winner_name}</td>
                                        <td style={{ color: "var(--muted)" }}>{i18nManager.queryCountryCurrency(winnerCurrency ?? "NGN", "currency_code", "currency_sign")} {StringHelper.toPresentableMoneyValue(winnerPrizePoolAmount)}</td>
                                    </tr>);
                                })}
                            </tbody>
                        </table>
                    </>)}
                </div>
            </div>
        </div>
    );
}

interface TournamentsProps {
    website: IHostWebsite;
}

function Tournaments(props: TournamentsProps) {

    const { website } = props;
    const omnehostService = OmneHostService.getInstance();
    const [selectedTournament, setSelectedTournament] = React.useState<ITournament>();
    const [tournaments, setTournaments] = React.useState<ApiResponseData<ITournament>>();
    const { page, setPage, tab, setTab, tournament, setTournament } = useSearchParams({ defaultInit: window.location.search, params: { page: 1, tab: "", tournament: "" } });

    React.useEffect(() => {
        if (!tournament) {
            setTournament(undefined);
        }
    }, [tournament]);
    React.useEffect(fetchInformation, [omnehostService, page, tab]);

    if (selectedTournament) {
        return (<TournamentModal hostId={website.host.external_id} tournament={selectedTournament} onClose={() => {
            setSelectedTournament(undefined);
            setTournament(undefined);
        }} />);
    }

    return (
        <div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h1 className={styles["sec-title"]}>All <em>Tournaments</em></h1>
            <p className={styles["sec-sub"]}>Every competition we've hosted — past, present and future</p>

            <div className={styles["tabs"]}>
                {([["", "All"], [TournamentStatus.ONGOING, "Live"], [TournamentStatus.UPCOMING, "Upcoming"], [TournamentStatus.ENDED, "Past"]] as [TournamentStatus | "all", string][]).map(([v, l]) => (
                    <button
                        key={v}
                        className={`${styles["tab-btn"]} ${tab === v ? styles["active"] : ""}`}
                        onClick={() => setTab(v)}
                    >
                        {l}
                    </button>
                ))}
            </div>

            {(!!tournaments?.content)
                ? ((tournaments.content && tournaments.content?.length > 0)
                    ? (<div className={styles["tournament-grid"]}>
                        {tournaments.content!.map((t) => (<TournamentCard key={t.external_id} tournament={t} onSelect={(t) => {
                            setSelectedTournament(t);
                            setTournament(t.external_id);
                        }} />))}
                    </div>)
                    : (Utils.emptyState()))
                : (Utils.loadingState())}

            <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={tournaments?.size} totalRecords={tournaments?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
                setPage(e.currentPage)
            }} template={{
                activePageLabel: () => "",
                nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
            }} />
        </div>
    );

    function fetchInformation() {
        setTournaments({});
        omnehostService.queryHostTournaments(website.host.external_id, {
            page,
            status: tab,
            sort: "public_id,DESC",
        }).then(({ apiResponse, content }: SanitizedResponse<ITournament>) => {
            setTournaments(apiResponse.data!);
            for (const v of (content ?? [])) {
                if (tournament && tournament === v.external_id) {
                    setSelectedTournament(v);
                    break;
                }
            }
        }).catch(BaseService.reportError);
    }

}

export default Tournaments;
